import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { 
  Menu, X, Sun, Moon, Globe, ChevronDown, Check,
  Landmark, TrendingUp, Users, ArrowRight, ArrowLeftRight,
  FileText, Calendar, ScrollText, Scale,
  Newspaper, BarChart3, MapPin, Info, Target, Heart
} from 'lucide-react';

const Header = () => {
  const { language, setLanguage, languages, languageNames } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPropositionsOpen, setIsPropositionsOpen] = useState(false);
  const [isRepubliqueOpen, setIsRepubliqueOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  
  // Refs for outside click detection
  const headerRef = useRef(null);
  
  // Scroll progress
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close all menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsPropositionsOpen(false);
    setIsRepubliqueOpen(false);
    setIsLanguageOpen(false);
  }, [location.pathname]);

  // Outside click handler to close all dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setIsPropositionsOpen(false);
        setIsRepubliqueOpen(false);
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to close other menus when opening one (mutual exclusivity)
  const openPropositionsMenu = () => {
    setIsPropositionsOpen(true);
    setIsRepubliqueOpen(false);
    setIsLanguageOpen(false);
  };

  const openRepubliqueMenu = () => {
    setIsRepubliqueOpen(true);
    setIsPropositionsOpen(false);
    setIsLanguageOpen(false);
  };

  const openLanguageMenu = () => {
    setIsLanguageOpen(true);
    setIsPropositionsOpen(false);
    setIsRepubliqueOpen(false);
  };

  const closeAllMenus = () => {
    setIsPropositionsOpen(false);
    setIsRepubliqueOpen(false);
    setIsLanguageOpen(false);
  };

  // Translations
  const t = {
    fr: {
      ourPosture: 'Notre Posture',
      proposals: 'Propositions',
      institutional: 'Institutionnelle',
      economic: 'Économique',
      social: 'Sociale',
      rebuildState: 'Refonder l\'État',
      freeTerritorries: 'Libérer les territoires',
      serveCitizens: 'Servir les citoyens',
      fourthRepublic: '4ᵉ République',
      constitution: 'La Constitution',
      comparison: '3ᵉ vs 4ᵉ',
      fourthRepublicProject: 'Projet de 4ᵉ République',
      vision: 'Vision',
      manifesto: 'Le Manifeste',
      foundingVision: 'Notre vision fondatrice',
      territories: 'Les Territoires',
      autonomousRegions: '31 régions autonomes',
      terrain: 'Terrain',
      viewAllProposals: 'Voir toutes les propositions',
      proposalsCount: '15 propositions • 3 axes',
      newArchitecture: 'Une nouvelle architecture pour la Côte d\'Ivoire',
      join: 'Rejoindre',
      joinMovement: 'Rejoindre le mouvement',
      organigramme: 'Organigramme',
      organigrammeDesc: 'Notre direction'
    },
    en: {
      ourPosture: 'Our Posture',
      proposals: 'Proposals',
      institutional: 'Institutional',
      economic: 'Economic',
      social: 'Social',
      rebuildState: 'Rebuild the State',
      freeTerritorries: 'Free territories',
      serveCitizens: 'Serve citizens',
      fourthRepublic: '4th Republic',
      constitution: 'The Constitution',
      comparison: '3rd vs 4th',
      fourthRepublicProject: '4th Republic Project',
      vision: 'Vision',
      manifesto: 'The Manifesto',
      foundingVision: 'Our founding vision',
      territories: 'Territories',
      autonomousRegions: '31 autonomous regions',
      terrain: 'Field',
      viewAllProposals: 'View all proposals',
      proposalsCount: '15 proposals • 3 axes',
      newArchitecture: 'A new architecture for Côte d\'Ivoire',
      join: 'Join',
      joinMovement: 'Join the movement',
      organigramme: 'Organization',
      organigrammeDesc: 'Our leadership'
    },
    pt: {
      ourPosture: 'A Nossa Postura',
      proposals: 'Propostas',
      institutional: 'Institucional',
      economic: 'Económica',
      social: 'Social',
      rebuildState: 'Refundar o Estado',
      freeTerritorries: 'Libertar os territórios',
      serveCitizens: 'Servir os cidadãos',
      fourthRepublic: '4ª República',
      constitution: 'A Constituição',
      comparison: '3ª vs 4ª',
      fourthRepublicProject: 'Projeto da 4ª República',
      vision: 'Visão',
      manifesto: 'O Manifesto',
      foundingVision: 'A nossa visão fundadora',
      territories: 'Os Territórios',
      autonomousRegions: '31 regiões autónomas',
      terrain: 'Terreno',
      viewAllProposals: 'Ver todas as propostas',
      proposalsCount: '15 propostas • 3 eixos',
      newArchitecture: 'Uma nova arquitetura para a Costa do Marfim',
      join: 'Aderir',
      joinMovement: 'Aderir ao movimento',
      organigramme: 'Organigrama',
      organigrammeDesc: 'A nossa direção'
    }
  };

  const content = t[language] || t.fr;

  // Main navigation items with icons for consistency
  const mainNav = [
    { 
      id: 'ada-ci',
      path: '/a-propos', 
      label: 'ADA',
      desc: language === 'fr' ? 'Qui sommes-nous' : language === 'en' ? 'Who we are' : 'Quem somos',
      icon: Info,
      color: 'bg-[#5B2D8E]'
    },
    { 
      id: 'posture',
      path: '/notre-posture', 
      label: content.ourPosture,
      desc: language === 'fr' ? 'Notre vision politique' : language === 'en' ? 'Our political vision' : 'Nossa visão política',
      icon: Target,
      color: 'bg-[#F7C948]'
    },
    { 
      id: 'organigramme',
      path: '/organigramme', 
      label: content.organigramme,
      desc: content.organigrammeDesc,
      icon: Users,
      color: 'bg-[#0D9488]'
    },
  ];

  const propositionsSubNav = [
    { 
      id: 'institutional',
      path: '/propositions#institutional', 
      label: content.institutional,
      desc: content.rebuildState,
      icon: Landmark,
      color: 'bg-[#5B2D8E]'
    },
    { 
      id: 'economic',
      path: '/propositions#economic', 
      label: content.economic,
      desc: content.freeTerritorries,
      icon: TrendingUp,
      color: 'bg-[#F7C948]'
    },
    { 
      id: 'social',
      path: '/propositions#social', 
      label: content.social,
      desc: content.serveCitizens,
      icon: Users,
      color: 'bg-[#0D9488]'
    },
  ];

  const republiqueSubNav = [
    {
      id: 'constitution',
      path: '/constitution',
      label: content.constitution,
      desc: content.fourthRepublicProject,
      icon: ScrollText,
      color: 'bg-[#5B2D8E]'
    },
  ];

  // New section for Vision (Manifeste + Territoires)
  const visionSubNav = [
    {
      id: 'manifeste',
      path: '/manifeste',
      label: content.manifesto,
      desc: content.foundingVision,
      icon: FileText,
      color: 'bg-[#F7C948]'
    },
    {
      id: 'territoires',
      path: '/territoires',
      label: content.territories,
      desc: content.autonomousRegions,
      icon: Scale,
      color: 'bg-[#0D9488]'
    },
  ];

  const quickLinks = [
    { 
      id: 'tribunes',
      path: '/tribunes', 
      label: 'Tribunes', 
      desc: language === 'fr' ? 'Publications & analyses' : language === 'en' ? 'Publications & analyses' : 'Publicações e análises',
      icon: Newspaper,
      color: 'bg-[#0D9488]'
    },
    { 
      id: 'scorecard',
      path: '/scorecard', 
      label: 'Scorecard',
      desc: language === 'fr' ? 'Suivi des engagements' : language === 'en' ? 'Commitment tracking' : 'Acompanhamento de compromissos',
      icon: BarChart3,
      color: 'bg-[#5B2D8E]'
    },
    { 
      id: 'terrain',
      path: '/terrain', 
      label: content.terrain,
      desc: language === 'fr' ? 'Actions & événements' : language === 'en' ? 'Actions & events' : 'Ações e eventos',
      icon: MapPin,
      color: 'bg-[#F7C948]'
    },
  ];

  return (
    <>
      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 z-[60] origin-left"
        style={{ 
          scaleX,
          background: isDark 
            ? 'linear-gradient(90deg, #5B2D8E, #E5A700)' 
            : 'linear-gradient(90deg, #5B2D8E, #E5A700)'
        }}
      />

      <motion.header
        ref={headerRef}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'py-2' 
            : 'py-4'
        }`}
      >
        <div className="container-custom">
          <nav 
            className={`flex items-center justify-between px-6 py-3 rounded-full transition-all duration-500 ${
              isScrolled
                ? isDark 
                  ? 'bg-[#1A0F2E]/90 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/20'
                  : 'bg-white/90 backdrop-blur-xl border border-black/5 shadow-2xl shadow-black/5'
                : isDark
                  ? 'bg-[#1A0F2E]/60 backdrop-blur-md border border-white/5'
                  : 'bg-white/60 backdrop-blur-md border border-black/5'
            }`}
          >
            {/* Logo */}
            <Link 
              to="/accueil" 
              className="flex items-center group"
              data-testid="logo-link"
            >
              <img
                src="/logo-ada.png"
                alt="ADA Logo"
                className="h-10 min-w-[30px] transition-all duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Beta Badge - Centered */}
            <div className="absolute left-1/2 transform -translate-x-1/2 lg:relative lg:left-0 lg:transform-none lg:ml-2">
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-sm animate-pulse">
                Beta
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-0.5">
              {/* Notre Posture */}
              <Link
                to="/notre-posture"
                className={`whitespace-nowrap px-3 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                  location.pathname === '/notre-posture'
                    ? isDark ? 'bg-white/10 text-white' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-black/5 dark:hover:bg-white/5'
                }`}
              >
                Posture
              </Link>

              {/* Organigramme */}
              <Link
                to="/organigramme"
                className={`whitespace-nowrap px-3 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                  location.pathname === '/organigramme'
                    ? isDark ? 'bg-white/10 text-white' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-black/5 dark:hover:bg-white/5'
                }`}
              >
                Équipe
              </Link>

              {/* Propositions Mega Menu Trigger */}
              <div className="relative">
                <button
                  onClick={() => isPropositionsOpen ? closeAllMenus() : openPropositionsMenu()}
                  onMouseEnter={openPropositionsMenu}
                  className={`whitespace-nowrap flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                    location.pathname === '/propositions' || isPropositionsOpen
                      ? isDark ? 'bg-[#F7C948]/20 text-[#F7C948]' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
                      : 'text-theme-secondary hover:text-theme-primary'
                  }`}
                  data-testid="propositions-menu-trigger"
                >
                  Propositions
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isPropositionsOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Mega Menu */}
                <AnimatePresence>
                  {isPropositionsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      onMouseLeave={closeAllMenus}
                      className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[540px] p-6 rounded-sm border z-50 shadow-2xl ${
                        isDark 
                          ? 'bg-[#1A0F2E] border-white/10 shadow-black/50' 
                          : 'bg-white border-gray-200 shadow-black/10'
                      }`}
                    >
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {propositionsSubNav.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.id}
                              to={item.path}
                              onClick={closeAllMenus}
                              className={`group p-4 rounded-sm transition-all duration-300 ${
                                isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                              }`}
                              data-testid={`mega-menu-${item.id}`}
                            >
                              <div className={`w-10 h-10 ${item.color} rounded-sm flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <div className={`font-semibold text-sm mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.label}</div>
                              <div className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{item.desc}</div>
                            </Link>
                          );
                        })}
                      </div>
                      
                      <div className={`pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <Link
                          to="/propositions"
                          onClick={closeAllMenus}
                          className={`flex items-center justify-between p-3 rounded-sm transition-all ${
                            isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                          }`}
                          data-testid="mega-menu-view-all"
                        >
                          <div>
                            <div className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {content.viewAllProposals}
                            </div>
                            <div className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{content.proposalsCount}</div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-[#F7C948]" />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* 4ᵉ République - Dropdown menu with badge */}
              <div className="relative group">
                <button
                  className={`whitespace-nowrap flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-full transition-all duration-300 ${
                    location.pathname === '/constitution' || location.pathname === '/comparaison'
                      ? 'bg-[#5B2D8E] text-white'
                      : isDark 
                        ? 'bg-[#5B2D8E]/20 text-[#9B7FCE] hover:bg-[#5B2D8E]/30' 
                        : 'bg-[#5B2D8E]/10 text-[#5B2D8E] hover:bg-[#5B2D8E]/20'
                  }`}
                  data-testid="4e-republique-menu"
                >
                  4ᵉ République
                  <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" />
                </button>
                
                {/* Dropdown */}
                <div className={`absolute top-full left-0 mt-1 w-48 py-2 rounded-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 ${
                  isDark 
                    ? 'bg-[#1A0F2E] border-white/10' 
                    : 'bg-white border-gray-200 shadow-lg'
                }`}>
                  <Link
                    to="/constitution"
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                      location.pathname === '/constitution'
                        ? 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
                        : isDark ? 'text-white/80 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Scale className="w-4 h-4" />
                    {content.constitution}
                  </Link>
                  <Link
                    to="/comparaison"
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                      location.pathname === '/comparaison'
                        ? 'bg-[#F7C948]/10 text-[#D4880D]'
                        : isDark ? 'text-white/80 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <ArrowLeftRight className="w-4 h-4" />
                    {content.comparison}
                  </Link>
                </div>
              </div>

              {/* Manifeste - Direct link with gold accent */}
              <Link
                to="/manifeste"
                className={`whitespace-nowrap px-3 py-2 text-sm font-semibold rounded-full transition-all duration-300 ${
                  location.pathname === '/manifeste'
                    ? 'bg-[#F7C948] text-[#1A0F2E]'
                    : isDark 
                      ? 'bg-[#F7C948]/20 text-[#F7C948] hover:bg-[#F7C948]/30' 
                      : 'bg-[#F7C948]/20 text-[#B8860B] hover:bg-[#F7C948]/30'
                }`}
                data-testid="manifeste-link"
              >
                Manifeste
              </Link>

              {/* Territoires - Direct link */}
              <Link
                to="/territoires"
                className={`whitespace-nowrap px-3 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                  location.pathname === '/territoires'
                    ? isDark ? 'bg-[#0D9488]/20 text-[#0D9488]' : 'bg-[#0D9488]/10 text-[#0D9488]'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                data-testid="territoires-link"
              >
                Territoires
              </Link>

              {/* Quick Links - simplified */}
              <Link
                to="/tribunes"
                className={`whitespace-nowrap flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                  location.pathname === '/tribunes'
                    ? isDark ? 'bg-white/10 text-white' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-black/5 dark:hover:bg-white/5'
                }`}
              >
                <Newspaper className="w-3.5 h-3.5" />
                Tribunes
              </Link>
              <Link
                to="/scorecard"
                className={`whitespace-nowrap flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                  location.pathname === '/scorecard'
                    ? isDark ? 'bg-white/10 text-white' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-black/5 dark:hover:bg-white/5'
                }`}
              >
                <BarChart3 className="w-3.5 h-3.5" />
                Scorecard
              </Link>
              <Link
                to="/terrain"
                className={`whitespace-nowrap flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                  location.pathname === '/terrain'
                    ? isDark ? 'bg-white/10 text-white' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-black/5 dark:hover:bg-white/5'
                }`}
              >
                <MapPin className="w-3.5 h-3.5" />
                Terrain
              </Link>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-1">
              {/* Language Selector */}
              <div className="relative hidden sm:block">
                <button
                  onClick={() => isLanguageOpen ? closeAllMenus() : openLanguageMenu()}
                  className={`flex items-center gap-1 px-2 py-2 text-xs font-bold rounded-full transition-all ${
                    isDark 
                      ? 'bg-white/10 text-white hover:bg-white/20' 
                      : 'bg-black/5 text-theme-primary hover:bg-black/10'
                  }`}
                  data-testid="language-toggle"
                >
                  <Globe className="w-3.5 h-3.5" />
                  {language.toUpperCase()}
                </button>
                
                <AnimatePresence>
                  {isLanguageOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      className={`absolute top-full right-0 mt-2 py-2 w-36 rounded-sm border z-50 shadow-xl ${
                        isDark 
                          ? 'bg-[#1A0F2E] border-white/10' 
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      {languages.map((lang) => (
                        <button
                          key={lang}
                          onClick={() => { setLanguage(lang); closeAllMenus(); }}
                          className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors ${
                            language === lang 
                              ? 'text-[#F7C948]' 
                              : isDark 
                                ? 'text-white/70 hover:text-white hover:bg-white/5' 
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          {languageNames[lang]}
                          {language === lang && <Check className="w-4 h-4" />}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-all ${
                  isDark 
                    ? 'bg-white/10 text-[#E5A700] hover:bg-white/20' 
                    : 'bg-black/5 text-[#5B2D8E] hover:bg-black/10'
                }`}
                data-testid="theme-toggle"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>

              {/* Single CTA Button - Rejoindre */}
              <Link
                to="/rejoindre"
                className={`hidden md:flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-full transition-all duration-300 hover:-translate-y-0.5 ${
                  isDark
                    ? 'bg-[#F7C948] text-[#1A0F2E] hover:bg-[#FFD966]'
                    : 'bg-[#F7C948] text-[#1A0F2E] hover:bg-[#E5A700]'
                }`}
                style={{ boxShadow: '0 4px 15px rgba(247, 201, 72, 0.3)' }}
                data-testid="header-join-cta"
              >
                {content.join}
                <ArrowRight className="w-4 h-4" />
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-2.5 rounded-full transition-all ${
                  isDark ? 'bg-white/10 text-white' : 'bg-black/5 text-theme-primary'
                }`}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </nav>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed inset-0 z-[55] pt-6 px-4 lg:hidden overflow-y-auto ${
              isDark ? 'bg-[#1A0F2E]' : 'bg-white'
            }`}
          >
            {/* Mobile Menu Header with Close Button */}
            <div className="flex items-center justify-between mb-6">
              <Link to="/" className="flex items-center gap-3" onClick={() => setIsMobileMenuOpen(false)}>
                <img
                  src="/logo-ada.png"
                  alt="ADA Logo"
                  className="h-10 min-w-[30px]"
                />
                <span className="text-lg font-bold text-theme-primary">ADA</span>
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className={`p-2.5 rounded-sm transition-all ${
                  isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-black/5 text-theme-primary hover:bg-black/10'
                }`}
                data-testid="mobile-menu-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="container-custom">
              <nav className="flex flex-col gap-2">
                {mainNav.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                        location.pathname === item.path
                          ? isDark ? 'bg-white/10' : 'bg-[#5B2D8E]/10'
                          : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-10 h-10 ${item.color} rounded-sm flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-theme-primary">{item.label}</div>
                        <div className="text-xs text-theme-muted">{item.desc}</div>
                      </div>
                    </Link>
                  );
                })}

                <div className={`my-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} />

                <div className="text-xs font-bold uppercase tracking-widest text-theme-muted mb-2 px-4">
                  {content.proposals}
                </div>
                
                {propositionsSubNav.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                        isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-10 h-10 ${item.color} rounded-sm flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-theme-primary">{item.label}</div>
                        <div className="text-xs text-theme-muted">{item.desc}</div>
                      </div>
                    </Link>
                  );
                })}

                <div className={`my-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} />

                <div className="text-xs font-bold uppercase tracking-widest text-theme-muted mb-2 px-4">
                  {content.fourthRepublic}
                </div>
                
                {republiqueSubNav.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                        isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-10 h-10 ${item.color} rounded-sm flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-theme-primary">{item.label}</div>
                        <div className="text-xs text-theme-muted">{item.desc}</div>
                      </div>
                    </Link>
                  );
                })}

                <div className={`my-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} />

                <div className="text-xs font-bold uppercase tracking-widest text-theme-muted mb-2 px-4">
                  {content.vision}
                </div>
                
                {visionSubNav.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                        isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-10 h-10 ${item.color} rounded-sm flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-theme-primary">{item.label}</div>
                        <div className="text-xs text-theme-muted">{item.desc}</div>
                      </div>
                    </Link>
                  );
                })}

                <div className={`my-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} />

                {quickLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                        isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-10 h-10 ${item.color} rounded-sm flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-theme-primary">{item.label}</div>
                        <div className="text-xs text-theme-muted">{item.desc}</div>
                      </div>
                    </Link>
                  );
                })}

                <div className="mt-6 flex flex-col gap-3">
                  <Link
                    to="/don"
                    className="flex items-center justify-center gap-2 w-full px-6 py-4 text-lg font-bold rounded-sm transition-all bg-[#0D9488] text-white hover:bg-[#0b7c72]"
                    data-testid="mobile-donate-cta"
                  >
                    <Heart className="w-5 h-5" />
                    {language === 'fr' ? 'Faire un don' : language === 'en' ? 'Donate' : 'Doar'}
                  </Link>
                  <Link
                    to="/rejoindre"
                    className="flex items-center justify-center gap-2 w-full px-6 py-4 text-lg font-bold rounded-sm transition-all bg-[#F7C948] text-[#0A0510] hover:bg-[#E5A700]"
                    data-testid="mobile-join-cta"
                  >
                    {content.joinMovement}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
